// The sending half of channel 2 — `extension-message.service.ng1.ts`, ported.
//
// The SPA runs inside an iframe the content script injected, and everything it cannot do for
// itself it asks the parent for: which ATS it is on, where the handle sits, whether the panel
// is open, and — the other direction — telling the parent to close it.
//
// Beside `iframe-messages.ts` because they are two ends of one protocol, and dependency-free
// for the same reason: the content script imports the other half, and whatever it imports
// runs inside third-party ATS pages (CLAUDE.md).
import { createNotification, createRequest, isResponseTo, type IFrameMessage } from './iframe-messages.ts'

/**
 * ⚠️ `-1` MEANS NEVER TIME OUT, AND IT IS UPSTREAM'S OWN CONSTANT with upstream's own warning
 * beside it ("use with caution"). A request that never settles holds its listener and its
 * promise forever; the only honest use is a message whose answer is genuinely unbounded.
 */
export const NO_RESPONSE_TIMEOUT = -1

/** Upstream's default. Long enough for a parent doing storage work, short enough to notice. */
export const DEFAULT_RESPONSE_TIMEOUT_MS = 3000

/**
 * The slice of `Window` this needs, declared structurally.
 *
 * ⚠️ `packages/protocol` HAS NO DOM LIB, AND THAT IS DELIBERATE: the service worker imports
 * this package and has no `window`, and so do the Node tools under `tools/`. Adding `DOM` to
 * the package's libs to type one file would hand every other consumer a global `window` that
 * does not exist where they run. A real `Window` satisfies this interface structurally.
 */
export interface FrameWindow {
  parent: FrameWindow
  location?: { search?: string }
  postMessage: (message: unknown, targetOrigin: string) => void
  addEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void
  removeEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void
}

export interface IFrameClientOptions {
  /** Defaults to the ambient window. Injected so this is testable without a page. */
  window?: FrameWindow
  /** Defaults to the `?target=` the parent put in the frame's URL — see `resolveTarget`. */
  target?: string
}

/** The ambient window, or undefined where there is none (worker, Node). */
function ambientWindow(): FrameWindow | undefined {
  return (globalThis as { window?: FrameWindow }).window
}

/**
 * Which handler on the parent page this document's messages are addressed to.
 *
 * ⚠️ THE PARENT NAMES IT, IN THE FRAME'S OWN URL — IT IS NOT A CONSTANT. `chrome-extension.jsp`
 * does `window.targetFrame = urlParams.get('target')` and every `sendToParent` upstream passes
 * `target: (window as any).targetFrame`; the content script builds the src as
 * `…/chrome-extension/myaccount?target=shortlist&frameRole=shortlist`
 * (`woo-shortlist.component.ts:378`, where `name = 'shortlist'`).
 *
 * ⚠️ AND THE SECOND FRAME IS GIVEN NONE, DELIBERATELY. The notifications frame's src carries
 * `frameRole=notifications` and NO `target` (:408), so its messages travel with
 * `target: undefined` and the shortlist handler's `canHandleType` rejects them — that is how
 * two frames share one page listener without answering each other's requests. Defaulting to
 * `shortlist` here would make the notifications frame impersonate the shortlist one, and the
 * symptom would be an answer arriving in the wrong frame rather than an error anywhere.
 *
 * So: the URL's value, or `undefined`. A caller that knows better passes `target` explicitly.
 */
export function resolveTarget(win: FrameWindow | undefined = ambientWindow()): string | undefined {
  return targetIn(win?.location?.search)
}

/**
 * The target this document was LOADED with, captured at import.
 *
 * ⚠️ READ ONCE, AT MODULE LOAD, BECAUSE THE SPA REWRITES ITS OWN URL AND UPSTREAM DOES NOT.
 * `chrome-extension.jsp` runs `window.targetFrame = urlParams.get('target')` inline in the
 * document, so it reads the URL the frame was given. Our frame is given
 * `/chrome-extension/myaccount?target=shortlist&frameRole=shortlist` and React Router
 * immediately redirects a signed-out visitor to `/chrome-extension/auth/login` — same
 * document, new history entry, NO QUERY. A lazily-constructed client then reads a search
 * string of `''` and addresses every message to nobody.
 *
 * Measured, not reasoned about: `tools/dev/close-dialog-smoke.mjs` printed
 * `target in url: (none)` against a frame whose src had carried it, and the close button did
 * nothing. The module evaluates before any routing can run, which is exactly upstream's
 * moment.
 */
const INITIAL_TARGET = resolveTarget()

/** Pure parser, so the lookup above and an injected window share one implementation. */
function targetIn(search: string | undefined): string | undefined {
  if (typeof search !== 'string' || search.length === 0) return undefined
  // Hand-parsed rather than via `URLSearchParams`: this package is imported by the content
  // script, and keeping it free of platform globals is the same rule that keeps it DOM-free.
  for (const pair of search.replace(/^\?/, '').split('&')) {
    const [key, value] = pair.split('=')
    // `+` first: the producer is `URLSearchParams`, which encodes a space that way, and
    // `decodeURIComponent` leaves it alone.
    if (key === 'target' && value !== undefined && value.length > 0) return decodeURIComponent(value.replace(/\+/g, ' '))
  }
  return undefined
}

/**
 * ⚠️ NOT IN AN IFRAME IS A NORMAL STATE, NOT AN ERROR. Upstream checks `$window ===
 * $window.parent` and returns quietly: the same SPA serves `/chrome-extension/**` as a
 * top-level tab — the OAuth round-trip, `/post`, the uninstall page — where there is no
 * parent to talk to. `postMessage` to itself would be answered by nobody, and a thrown error
 * would take down a screen that is working correctly.
 */
export function isInIFrame(win: FrameWindow | undefined = ambientWindow()): boolean {
  return win !== undefined && win !== win.parent
}

export class IFrameClient {
  private readonly win: FrameWindow | undefined
  private readonly target: string | undefined

  constructor(options: IFrameClientOptions = {}) {
    this.win = options.window ?? ambientWindow()
    // An injected window is read live — that is a test, and its URL does not move. The
    // ambient one uses the value captured at import; see `INITIAL_TARGET`.
    this.target = options.target ?? (options.window === undefined ? INITIAL_TARGET : resolveTarget(options.window))
  }

  /** Fire-and-forget. Silently does nothing outside an iframe — see `isInIFrame`. */
  send<T>(type: string, data?: T): void {
    const win = this.win
    if (win === undefined || !isInIFrame(win)) return
    win.parent.postMessage(createNotification({ type, data, target: this.target }), '*')
  }

  /**
   * Ask, and wait for the correlated answer.
   *
   * ⚠️ IT REJECTS OUTSIDE AN IFRAME RATHER THAN HANGING. Upstream's `sendToParent` returns
   * quietly there and `sendToParentWithResponse` then waits for its timeout before rejecting
   * — three seconds of nothing on every call, on screens where there is no parent by design.
   * Rejecting immediately says the same thing sooner, and every caller already handles a
   * rejection because the parent may be an older extension that does not know the event.
   *
   * ⚠️ AND THE LISTENER IS REMOVED ON EVERY PATH. The answer, the timeout and the
   * no-parent case all dispose it: a listener per unanswered request is how a long-lived
   * frame accumulates thousands of them, each holding its closure.
   */
  async request<R, T = unknown>(type: string, data?: T, timeoutMs = DEFAULT_RESPONSE_TIMEOUT_MS): Promise<R> {
    const win = this.win
    if (win === undefined || !isInIFrame(win)) {
      throw new Error(`cannot request '${type}': this document has no parent frame`)
    }

    const message = createRequest({ type, data, target: this.target })
    return new Promise<R>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined

      const onMessage = (event: { data: unknown }): void => {
        if (!isResponseTo(message, event.data)) return
        dispose()
        resolve((event.data as IFrameMessage<R>).data as R)
      }

      const dispose = (): void => {
        win.removeEventListener('message', onMessage)
        if (timer !== undefined) clearTimeout(timer)
      }

      win.addEventListener('message', onMessage)

      if (timeoutMs !== NO_RESPONSE_TIMEOUT) {
        timer = setTimeout(() => {
          dispose()
          // Named, unlike upstream's bare `reject()`. A rejection with no reason is what a
          // caller logs as `undefined`, and the two causes — an older extension that does
          // not know the event, and a parent that is not there — need different fixes.
          reject(new Error(`no response to '${type}' within ${String(timeoutMs)}ms`))
        }, timeoutMs)
      }

      win.parent.postMessage(message, '*')
    })
  }

  /** Subscribe to everything the parent sends unprompted. Returns its disposer. */
  onMessage(listener: (message: IFrameMessage) => void): () => void {
    const win = this.win
    if (win === undefined) return () => undefined
    const onMessage = (event: { data: unknown }): void => {
      const data: unknown = event.data
      if (typeof data !== 'object' || data === null || typeof (data as IFrameMessage).type !== 'string') return
      listener(data as IFrameMessage)
    }
    win.addEventListener('message', onMessage)
    return () => {
      win.removeEventListener('message', onMessage)
    }
  }
}

/**
 * The one every screen uses.
 *
 * ⚠️ LAZY, NOT A MODULE-LEVEL `new`. The constructor reads `globalThis.window`, which does not
 * exist under vitest or any other Node consumer — a module-level instance would capture
 * `undefined` at IMPORT time, so merely importing this file from a test would hand every
 * caller a client whose window is gone. Constructed on first use instead, where a window
 * exists by definition.
 */
let shared: IFrameClient | undefined

export function iframeClient(): IFrameClient {
  shared ??= new IFrameClient()
  return shared
}
